import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation, Outlet } from "react-router-dom";

// guards
import LoginGuard from "../guards/LoginGuard";
import LoginWebGuard from "../guards/LoginWebGuard";
import AuthGuard from "../guards/AuthGuard";
import GuestGuard from "../guards/GuestGuard";
import AuthNoGuestGuard from "../guards/AuthNoGuestGuard";
import AdminGuard from "../guards/AdminGuard";
// pages
import Logout from "../pages/auth/Logout";
import LanguageDetector from "../pages/LanguageDetector";
import LoginWeb from "../pages/auth/LoginWeb";
import PasswordRequestTab from "../components/auth/PasswordRequestTab";
import PasswordResetTab from "../components/auth/PasswordResetTab";
import QuotationList from "../pages/QuotationList";
import AdminQuotationList from "../pages/admin/QuotationList";
import UserList from "../pages/admin/UserList";
import UserProfile from "../pages/UserProfile";
// layouts
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
import AuthLayout from "../layouts/AuthLayout";
// components
import LoadingScreen from "../components/LoadingScreen";
import LoginRegisterTabs from "../components/auth/LoginRegisterTabs";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const isDevelopment = typeof import.meta !== 'undefined' && import.meta.env?.MODE === "development";
  
  return useRoutes([
    {
      path: "/",
      element: <Navigate to="/dashboard/planner/create" replace />,
    },
    {
      path: "lang/:lang",
      element: <LanguageDetector />,
    },
    // Auth Routes
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <LoginGuard>
              <AuthLayout component={<LoginRegisterTabs />} />
            </LoginGuard>
          ),
        },
        {
          path: "login-web",
          element: (
            <LoginWebGuard>
              <LoginWeb />
            </LoginWebGuard>
          ),
        },
        {
          path: "logout",
          element: (
            <AuthGuard>
              <Logout />
            </AuthGuard>
          ),
        },
        {
          path: "password/request",
          element: (
            <LoginGuard>
              <AuthLayout component={<PasswordRequestTab />} />
            </LoginGuard>
          ),
        },
        {
          path: "password/reset/:otpCode",
          element: (
            <LoginGuard>
              <AuthLayout component={<PasswordResetTab />} />
            </LoginGuard>
          ),
        },
      ],
    },
    // Dashboard Routes Guest Users
    {
      path: "/dashboard",
      element: (
        <GuestGuard>
          <DashboardLayout />
        </GuestGuard>
      ),
      children: [
        {
          element: <Navigate to="/dashboard/planner/create" replace />,
          index: true,
        },
        {
          path: "planner",
          element: <Outlet />,
          children: [
            {
              element: <Navigate to="/dashboard/planner/create" replace />,
              index: true,
            },
            { path: "create", element: <PagePlanner new={true} /> },
          ],
        },
        {
          path: "new",
          element: <Navigate to="/dashboard/planner/create" replace />,
          index: true,
        },
      ],
    },
    // Dashboard Routes Non Guest Users
    {
      path: "/dashboard",
      element: (
        <AuthNoGuestGuard>
          <DashboardLayout />
        </AuthNoGuestGuard>
      ),
      children: [
        {
          path: "planner",
          element: <Outlet />,
          children: [
            { path: ":quotation_id/modify", element: <PagePlanner /> },
          ],
        },
        {
          path: "quotations",
          element: <QuotationList />,
        },
        {
          path: "profile",
          element: <UserProfile />,
        },
      ],
    },
    // Admin Routes
    {
      path: "/admin",
      element: (
        <AdminGuard>
          <DashboardLayout />
        </AdminGuard>
      ),
      children: [
        { element: <Navigate to="/admin/users" replace />, index: true },
        { path: "quotations", element: <AdminQuotationList /> },
        { path: "users", element: <UserList /> },
      ],
    },
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    // Development-only Sentry testing route
    ...(isDevelopment ? [{
      path: "/dev/sentry-test",
      element: <LogoOnlyLayout />,
      children: [
        { path: "", element: <SentryTestPage /> },
      ],
    }] : []),
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// Dashboard
const PagePlanner = Loadable(lazy(() => import("../pages/PagePlanner")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));

// Development only
const SentryTestPage = Loadable(lazy(() => import("../components/SentryTest")));
